import { useMemo } from "react";
import { ReadAgreementDetail } from "@/schemas/agreement";
import { AgreementTransitions } from "../context/AgreementContext";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { Module } from "@/constants";
import { AGREEMENT_STATUSES } from "@/types/agreement";

interface UseAgreementTransitionProps {
  data?: ReadAgreementDetail;
}

const defaultTransitions: AgreementTransitions = {
  canConfirm: false,
  canReject: false,
  canComment: false,
  canComplete: false,
  canSubmitForVerification: false,
  canPerformVerificationActions: false,
  canSendQuestionnaire: false,
  canUpdateContent: false,
  canUpdateData: false,
  canUpdateStatements: false,
  canUpdateTaxStatus: false,
  canDelete: false,
};

export const useAgreementTransition = ({
  data,
}: UseAgreementTransitionProps): AgreementTransitions => {
  const permissions = useModulePermissions(Module.Agreements);

  return useMemo((): AgreementTransitions => {
    if (!data) {
      return defaultTransitions;
    }

    const allowed = data.allowed_new_statuses || [];

    const canConfirm =
      permissions.agreements_confirm &&
      allowed.includes(AGREEMENT_STATUSES.AGREEMENT_CONFIRMED);
    const canReject =
      permissions.agreements_reject &&
      allowed.includes(AGREEMENT_STATUSES.AGREEMENT_REJECTED);
    const canComment =
      permissions.agreements_comment &&
      allowed.includes(AGREEMENT_STATUSES.QUESTIONNAIRE_TO_CORRECT);

    const canComplete =
      permissions.agreements_complete &&
      allowed.includes(AGREEMENT_STATUSES.AGREEMENT_COMPLETED);
    const canUpdateContent =
      permissions.agreements_amend_content &&
      allowed.includes(AGREEMENT_STATUSES.AGREEMENT_UPDATE_CONTENT);
    const canUpdateData =
      permissions.agreements_amend_data &&
      allowed.includes(AGREEMENT_STATUSES.AGREEMENT_AMEND_DATA);
    const canUpdateTaxStatus =
      permissions.agreements_amend_tax_status &&
      allowed.includes(AGREEMENT_STATUSES.AGREEMENT_AMEND_TAX_STATUS);

    const canDelete =
      permissions.agreements_delete &&
      data.agreement.agreement_status ===
        AGREEMENT_STATUSES.QUESTIONNAIRE_TO_FILL;

    return {
      canConfirm,
      canReject,
      canComment,
      canComplete,
      canSubmitForVerification: allowed.includes(
        AGREEMENT_STATUSES.QUESTIONNAIRE_TO_VERIFY,
      ),
      canPerformVerificationActions: canConfirm || canReject || canComment,
      canSendQuestionnaire: data.can_send_questionnaire_link,
      canUpdateContent,
      canUpdateData,
      canUpdateStatements:
        permissions.agreements_statements_update &&
        allowed.includes(AGREEMENT_STATUSES.AGREEMENT_AMEND_STATEMENTS),
      canUpdateTaxStatus,
      canDelete,
    };
  }, [data, permissions]);
};
